'use client'

import { cn } from '@dc-shared-web/lib'
import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react'

type TruncatedBlockProps = React.HTMLAttributes<HTMLDivElement> & {
  lines: number
  lineHeight: number
  enabled: boolean
}

export type TruncatedBlockRef = HTMLDivElement & { isOverflowing: boolean }

const TruncatedBlock = forwardRef<TruncatedBlockRef, TruncatedBlockProps>(
  (
    {
      children,
      lines,
      lineHeight,
      enabled,
      className,
      ...props
    }: TruncatedBlockProps,
    ref
  ) => {
    const [isOverflowing, setIsOverflowing] = useState(false)

    const divRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(
      ref,
      () => ({
        ...divRef.current!,
        isOverflowing,
      }),
      [isOverflowing]
    )

    useEffect(() => {
      setIsOverflowing(
        Boolean(
          divRef.current && divRef.current.scrollHeight > lineHeight * lines
        )
      )
    }, [children, enabled, lineHeight, lines])

    return (
      <div
        ref={divRef}
        style={enabled ? { maxHeight: `${lineHeight * lines}px` } : undefined}
        className={cn('tw-overflow-hidden tw-relative', className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)

TruncatedBlock.displayName = 'TruncatedBlock'

export default TruncatedBlock
